import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Search, Plus, User, Users, CalendarIcon, Clock, Sparkles, AlertTriangle, Loader2, PhoneOff, Baby, StickyNote, Pencil } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { useCustomerNotes } from "@/hooks/useCustomerNotes";
import { supabase } from "@/integrations/supabase/client";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { useEstablishmentQuestions } from "@/hooks/useEstablishmentQuestions";
import { selectApplicableQuestion, normalizeAnswers } from "@/lib/establishmentQuestionUtils";
import { EstablishmentQuestionField } from "@/components/EstablishmentQuestionField";
import { TerracePreferenceSelector } from "@/components/TerracePreferenceSelector";
import type { TerracePreference } from "@/types/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { CustomerData } from "@/types/client";
import { normalizePhoneForSearch, filterClients } from "@/lib/clientUtils";
import { Table, TableAvailability, TableWeekdayAvailability, CombinedTable, Reservation, TableBookablePeriod } from "@/types/table";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { format, isToday, addMinutes, isBefore } from "date-fns";
import { fr } from "date-fns/locale";
import { isTableAvailableOnDate, isTableInTemporaryBlockingPeriod } from "@/lib/tableAvailabilityUtils";
import { cn } from "@/lib/utils";
import { getOptimalityScore, findOptimalTable, getTablesInCombinations } from "@/lib/tableAssignmentUtils";
import { getBlockedTableIds, isTableAvailableForSlot, isCombinedTableAvailableForSlot } from "@/lib/reservationConflictUtils";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { useAuth } from "@/contexts/AuthContext";
import { generateAvailableTimes, parseTimeToMinutes, getReservationEndTime } from "@/lib/timeBookingUtils";
import { getTimeSlotsForDate } from "@/lib/timeSlotUtils";
import { getStaffSlotsForDate, isZoneOpenForReservation } from "@/lib/zoneScheduleUtils";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { useBookableZones } from "@/hooks/useBookableZones";
import { groupTablesBySpace } from "@/lib/tableSpaceGrouping";
import { TimeBookingGrid } from "@/components/TimeBookingGrid";
import { useServiceSlots } from "@/hooks/useServiceTimeCompare";
const reservationSchema = z.object({
  customerFirstName: z.string().trim().max(50, "Le prénom est trop long").optional().or(z.literal("")),
  customerLastName: z.string().trim().max(50, "Le nom est trop long").optional().or(z.literal("")),
  customerEmail: z.string().trim().email("Email invalide").max(255, "L'email est trop long").optional().or(z.literal("")),
  customerPhone: z.string().trim().max(20, "Numéro trop long").optional().or(z.literal("")),
  numberOfPeople: z.number().min(1, "Au moins 1 personne").max(20, "Maximum 20 personnes"),
  tableId: z.string().optional(),
  combinedTableId: z.string().optional(),
  notes: z.string().max(500, "Les notes sont trop longues").optional(),
  internalNotes: z.string().max(1000, "Les notes internes sont trop longues").optional(),
}).refine(data => (data.customerFirstName && data.customerFirstName.length >= 1) || (data.customerLastName && data.customerLastName.length >= 1), {
  message: "Veuillez saisir au moins un prénom ou un nom",
  path: ["customerFirstName"],
}).refine(data => data.tableId || data.combinedTableId, {
  message: "Veuillez sélectionner une table (individuelle ou combinée)",
});

interface NewReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeSlot?: string;
  date: Date;
  availableTables: Table[];
  combinedTables: CombinedTable[];
  allReservations: Reservation[];
  existingClients: CustomerData[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  onReservationCreate: (reservation: {
    customerFirstName: string;
    customerLastName: string;
    customerEmail?: string;
    customerPhone?: string;
    numberOfPeople: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    terracePreference?: TerracePreference;
    tableId?: string;
    combinedTableId?: string;
    startTime: string;
    date: Date;
    notes?: string;
    internalNotes?: string;
    durationMinutes?: number;
    source?: string;
    customAnswers?: { question: string; answers: string[] };
    status?: string;
    isRetroactive?: boolean;
    skipEmails?: boolean;
    skipTokens?: boolean;
    forceOverride?: boolean;
  }) => Promise<void> | void;
  preselectedCombinedTable?: CombinedTable;
  readOnlyMode?: boolean;
  preselectedTable?: Table;
  preselectedPersonCount?: number;
  preselectedChildren?: number;
  preselectedStroller?: boolean;
  establishmentId?: string;
  reservationSettings?: {
    bookingMode: 'slot' | 'time';
    reservationDurationMinutes: number;
    bookingIntervalMinutes: 15 | 30 | 60;
  };
  maxPeople?: number;
  bookablePeriods?: TableBookablePeriod[];
  childrenEnabled?: boolean;
  strollerEnabled?: boolean;
  childrenCapacityMode?: 'full' | 'half' | 'none';
  terraceEnabled?: boolean;
  preselectedTerracePreference?: TerracePreference;
  /** Active le mode saisie rétroactive dès l'ouverture (bouton "Réservation passée"). */
  defaultRetroactive?: boolean;
  /** Active par défaut le mode manager override (créneau fermé / table non-réservable). */
  defaultForceOverride?: boolean;
}

export function NewReservationDialog({
  open,
  onOpenChange,
  timeSlot: initialTimeSlot = '',
  date: initialDate,
  availableTables,
  combinedTables,
  allReservations,
  existingClients,
  availabilities,
  weekdayAvailabilities,
  onReservationCreate,
  preselectedCombinedTable,
  readOnlyMode = false,
  preselectedTable,
  preselectedPersonCount,
  preselectedChildren,
  preselectedStroller,
  establishmentId,
  reservationSettings,
  maxPeople,
  bookablePeriods = [],
  childrenEnabled = false,
  strollerEnabled = false,
  childrenCapacityMode = 'full',
  terraceEnabled = false,
  preselectedTerracePreference,
  defaultRetroactive = false,
  defaultForceOverride = false,
}: NewReservationDialogProps) {
  const { toast } = useToast();
  const { establishmentId: ctxEstablishmentId } = useEstablishment();
  const resolvedEstablishmentId = establishmentId || ctxEstablishmentId;
  const { customerNotes, getNotesForEmail, getCustomerIdForEmail, refetch: refetchCustomerNotes } = useCustomerNotes();
  const [editingCustomerNotes, setEditingCustomerNotes] = useState(false);
  const [customerNotesValue, setCustomerNotesValue] = useState("");
  const [savingCustomerNotes, setSavingCustomerNotes] = useState(false);
  
  // Build conflict options based on reservation settings (TIME mode support)
  const serviceSlots = useServiceSlots();
  const conflictOptions = reservationSettings?.bookingMode === 'time'
    ? { 
        bookingMode: 'time' as const, 
        reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120,
        serviceSlots,
      }
    : undefined;
  const [clientMode, setClientMode] = useState<"existing" | "new">("new");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<CustomerData | null>(null);
  
  // Date and time slot state
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(initialTimeSlot);

  // Question de l'établissement (facultative côté équipe)
  const { questions: establishmentQuestions } = useEstablishmentQuestions({
    establishmentId: resolvedEstablishmentId,
  });
  const applicableQuestion = useMemo(
    () => selectApplicableQuestion(establishmentQuestions, selectedDate),
    [establishmentQuestions, selectedDate]
  );
  const [customAnswers, setCustomAnswers] = useState<string[]>([]);
  useEffect(() => {
    setCustomAnswers([]);
  }, [applicableQuestion?.id]);
  // Saisie rétroactive manager : autorise date passée (jusqu'à 90 j), statut auto = "arrivé",
  // aucun email ni token client. Désactivé par défaut → comportement 100 % inchangé.
  const [isRetroactive, setIsRetroactive] = useState(false);
  // Mode "manager override" (créneau fermé, jour fermé, table non-réservable).
  // Serveur : le flag n'est effectif que si l'appelant est authentifié admin/staff/super_admin.
  // Aucun email ni token client. Désactivé par défaut → aucun impact sur le flux client.
  const [forceOverride, setForceOverride] = useState(false);

  // Track open transition to avoid resetting state on every Dashboard re-render
  const prevOpenRef = useRef(false);

  // Sync state with props ONLY when dialog transitions from closed to open
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedDate(initialDate);
      setSelectedTimeSlot(initialTimeSlot);
      setIsRetroactive(defaultRetroactive);
      setForceOverride(defaultForceOverride);
      
      
      // En mode readOnly, pré-remplir avec les données passées
      if (readOnlyMode) {
        if (preselectedTable) {
          setSelectedTableId(preselectedTable.id);
          setSelectedTableType('single');
        }
        if (preselectedCombinedTable) {
          setSelectedTableId(preselectedCombinedTable.id);
          setSelectedTableType('combined');
        }
        if (preselectedPersonCount) {
          setNumberOfPeople(preselectedPersonCount.toString());
        }
        setNumberOfChildren(preselectedChildren ?? 0);
        setHasStroller(preselectedStroller ?? false);
        setTerracePreference(preselectedTerracePreference ?? 'no_preference');
      }
      // Pré-sélection table combinée (mode normal)
      else if (preselectedCombinedTable) {
        setSelectedTableId(preselectedCombinedTable.id);
        setSelectedTableType('combined');
        setNumberOfPeople(preselectedCombinedTable.totalCapacity.toString());
      }
      // Pré-sélection table simple (mode normal)
      else if (availableTables.length === 1) {
        setSelectedTableId(availableTables[0].id);
        setSelectedTableType('single');
      }
    }
    prevOpenRef.current = open;
  }, [open, initialDate, initialTimeSlot, availableTables, preselectedCombinedTable, readOnlyMode, preselectedTable, preselectedPersonCount, preselectedChildren, preselectedStroller, preselectedTerracePreference, defaultRetroactive, defaultForceOverride]);
  
  // Form state for new client
  const [customerFirstName, setCustomerFirstName] = useState("");
  const [customerLastName, setCustomerLastName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [numberOfPeople, setNumberOfPeople] = useState("2");
  const [selectedTableId, setSelectedTableId] = useState("");
  const [selectedTableType, setSelectedTableType] = useState<'single' | 'combined'>('single');
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noEmail, setNoEmail] = useState(false);
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [terracePreference, setTerracePreference] = useState<TerracePreference>('no_preference');

  // Derived filtering variables for children/stroller constraints
  const hasChildrenActive = childrenEnabled && numberOfChildren > 0;
  const strollerActive = hasStroller && strollerEnabled;
  const capacityMode = childrenCapacityMode;
  const [customDuration, setCustomDuration] = useState<number | null>(null);
  const [source, setSource] = useState("");

  // Auto-adjust children count when people count decreases
  useEffect(() => {
    const people = parseInt(numberOfPeople);
    if (numberOfChildren >= people) {
      setNumberOfChildren(Math.max(0, people - 1));
    }
  }, [numberOfPeople, numberOfChildren]);

  // Fetch time slots from database
  const { session } = useAuth();
  const { timeSlots: supabaseTimeSlots, dateOverrides } = useSupabaseData({ establishmentId, useAuthenticatedData: true, session });
  
  // Horaires propres aux salles (additifs côté équipe)
  const { zoneWindows } = useZoneTimeWindows({ establishmentId, useAuthenticatedData: true });
  const { zones: bookableZones } = useBookableZones({ establishmentId, useAuthenticatedData: true });
  const activeZoneIds = useMemo(
    () => new Set(bookableZones.filter(z => z.isActive).map(z => z.id)),
    [bookableZones]
  );

  // Filter time slots based on selected date using getTimeSlotsForDate.
  // En mode manager override, on ignore le filtre "jour de la semaine" et on expose
  // TOUS les créneaux configurés → permet de saisir même un jour où aucun service n'est ouvert.
  const timeSlots = useMemo(() => {
    const slotsForDate = forceOverride
      ? supabaseTimeSlots
      : getStaffSlotsForDate(selectedDate, supabaseTimeSlots, dateOverrides, zoneWindows, activeZoneIds);
    return slotsForDate.map(ts => ({
      start: ts.start.substring(0, 5),
      end: ts.end.substring(0, 5),
      availableDays: ts.availableDays,
      durationOverrideMinutes: ts.durationOverrideMinutes,
    }));
  }, [supabaseTimeSlots, dateOverrides, selectedDate, forceOverride, zoneWindows, activeZoneIds]);

  // Detect if we're in TIME mode
  const isTimeMode = reservationSettings?.bookingMode === 'time';
  const bookingIntervalMinutes = reservationSettings?.bookingIntervalMinutes ?? 30;
  const reservationDurationMinutes = reservationSettings?.reservationDurationMinutes ?? 120;

  // Generate individual booking times for TIME mode
  const availableBookingTimes = useMemo(() => {
    if (!isTimeMode || timeSlots.length === 0) return [];
    
    const slotRanges = timeSlots.map(ts => ({ start: ts.start, end: ts.end, durationOverrideMinutes: ts.durationOverrideMinutes }));
    return generateAvailableTimes(slotRanges, bookingIntervalMinutes, reservationDurationMinutes);
  }, [isTimeMode, timeSlots, reservationSettings, reservationDurationMinutes]);

  // Calculate TIME mode availability for each individual time
  const timeAvailabilityForDialog = useMemo(() => {
    if (!isTimeMode || !selectedDate || availableBookingTimes.length === 0) return [];

    const now = new Date();
    const personsCount = numberOfPeople ? parseInt(numberOfPeople) : 0;

    return availableBookingTimes.map(time => {
      // Check if time is in the past for today
      // Bypass en saisie rétroactive ou mode manager override (le manager sait ce qu'il fait)
      if (isToday(selectedDate) && !isRetroactive && !forceOverride) {
        const [hours, minutes] = time.split(':').map(Number);
        const bookingTime = new Date(selectedDate);
        bookingTime.setHours(hours, minutes, 0, 0);
        if (isBefore(bookingTime, now)) {
          return { time, isAvailable: false, unavailableReason: 'hours' as const };
        }
      }


      // Get blocked table IDs for this specific time
      const blockedIds = getBlockedTableIds(allReservations, combinedTables, selectedDate, time, conflictOptions);
      
      // Tables simples disponibles pour cette heure
      const availableSingleTables = availableTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, bookablePeriods)) return false;
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null)) return false;
        return true;
      });

      // Combinaisons disponibles pour cette heure
      const availableCombined = combinedTables.filter(ct => {
        if (blockedIds.has(ct.id) || !ct.isActive) return false;
        if (hasChildrenActive && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        return ct.tableIds.every(tid => {
          const table = availableTables.find(t => t.id === tid);
          if (!table) return false;
          if (hasChildrenActive && table.allowsChildren === false) return false;
          if (strollerActive && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
          if (isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods)) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
        });
      });

      const isAvailable = availableSingleTables.length > 0 || availableCombined.length > 0;
      
      return {
        time,
        isAvailable,
        unavailableReason: isAvailable ? undefined : 'full' as const
      };
    });
  }, [isTimeMode, selectedDate, availableBookingTimes, numberOfPeople, allReservations, availableTables, combinedTables, availabilities, weekdayAvailabilities, conflictOptions, hasChildrenActive, strollerActive, bookablePeriods, isRetroactive, forceOverride]);

  // Calculate available time slots with availability indicators (SLOT mode)
  // Supports TIME mode when reservationSettings is provided
  const availableTimeSlots = useMemo(() => {
    if (!selectedDate) return timeSlots.map(slot => ({ ...slot, available: 0, isAvailable: true, canAccommodate: true, maxCapacity: 0 }));

    return timeSlots.map(slot => {
      const blockedIds = getBlockedTableIds(allReservations, combinedTables, selectedDate, slot.start, conflictOptions);
      
      // Tables simples disponibles pour ce créneau
      const availableSingleTables = availableTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, bookablePeriods)) return false;
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null);
      });

      // Combinaisons disponibles pour ce créneau
      const availableCombined = combinedTables.filter(ct => {
        if (blockedIds.has(ct.id) || !ct.isActive) return false;
        if (hasChildrenActive && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        return ct.tableIds.every(tid => {
          const table = availableTables.find(t => t.id === tid);
          if (!table) return false;
          if (hasChildrenActive && table.allowsChildren === false) return false;
          if (strollerActive && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
          if (isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods)) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
        });
      });

      const maxSingleCapacity = availableSingleTables.length > 0 
        ? Math.max(...availableSingleTables.map(t => t.capacity)) 
        : 0;
      const maxCombinedCapacity = availableCombined.length > 0 
        ? Math.max(...availableCombined.map(ct => ct.totalCapacity)) 
        : 0;
      const maxCapacity = Math.max(maxSingleCapacity, maxCombinedCapacity);

      // Si nombre de personnes sélectionné, vérifier si ce créneau peut l'accommoder
      const personsCount = numberOfPeople ? parseInt(numberOfPeople) : 0;
      const canAccommodate = personsCount === 0 || maxCapacity >= personsCount;

      return {
        ...slot,
        available: availableSingleTables.length + availableCombined.length,
        maxCapacity,
        isAvailable: availableSingleTables.length > 0 || availableCombined.length > 0,
        canAccommodate
      };
    });
  }, [selectedDate, numberOfPeople, allReservations, availableTables, combinedTables, availabilities, weekdayAvailabilities, conflictOptions, hasChildrenActive, strollerActive, bookablePeriods]);

  // Nombre max sélectionnable par l'équipe : plus grande capacité de l'établissement
  // (tables simples ou combinées), plancher à 20, garde-fou dur à 100.
  const maxSelectablePeople = useMemo(() => {
    const maxSingle = availableTables.length > 0 ? Math.max(...availableTables.map(t => t.capacity)) : 0;
    const maxCombined = combinedTables.length > 0 ? Math.max(...combinedTables.map(ct => ct.totalCapacity)) : 0;
    const current = parseInt(numberOfPeople || '0') || 0;
    return Math.min(100, Math.max(20, maxSingle, maxCombined, current));
  }, [availableTables, combinedTables, numberOfPeople]);

  // Calculate available person counts based on selected date and time slot
  const availablePersonCounts = useMemo(() => {
    if (!selectedDate) {
      return Array.from({ length: maxSelectablePeople }, (_, i) => ({ count: i + 1, isAvailable: true, hasMatchingTable: true }));
    }

    // TIME MODE: calculate capacity from actual available tables at the selected time
    if (isTimeMode && selectedTimeSlot) {
      const blockedIds = getBlockedTableIds(allReservations, combinedTables, selectedDate, selectedTimeSlot, conflictOptions);
      
      const availableSingleTables = availableTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, bookablePeriods)) return false;
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null);
      });

      const availableCombined = combinedTables.filter(ct => {
        if (blockedIds.has(ct.id) || !ct.isActive) return false;
        if (hasChildrenActive && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        return ct.tableIds.every(tid => {
          const table = availableTables.find(t => t.id === tid);
          if (!table) return false;
          if (hasChildrenActive && table.allowsChildren === false) return false;
          if (strollerActive && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
          if (isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods)) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
        });
      });

      const maxSingleCapacity = availableSingleTables.length > 0 
        ? Math.max(...availableSingleTables.map(t => t.capacity)) : 0;
      const maxCombinedCapacity = availableCombined.length > 0 
        ? Math.max(...availableCombined.map(ct => ct.totalCapacity)) : 0;
      const maxCapacity = Math.max(maxSingleCapacity, maxCombinedCapacity);

      return Array.from({ length: maxSelectablePeople }, (_, i) => {
        const count = i + 1;
      return { count, isAvailable: true, hasMatchingTable: maxCapacity >= count };
      });
    }

    // SLOT MODE: use raw time slot ranges (original behavior)
    const relevantSlots = selectedTimeSlot 
      ? availableTimeSlots.filter(s => s.start === selectedTimeSlot)
      : availableTimeSlots;

    const maxCapacity = relevantSlots.length > 0 
      ? Math.max(...relevantSlots.map(s => s.maxCapacity), 0)
      : 0;

    return Array.from({ length: maxSelectablePeople }, (_, i) => {
      const count = i + 1;
      const hasMatchingTable = relevantSlots.some(s => s.maxCapacity >= count && s.isAvailable);
      return { count, isAvailable: true, hasMatchingTable };
    });
  }, [selectedDate, selectedTimeSlot, availableTimeSlots, isTimeMode, allReservations, combinedTables, availableTables, availabilities, weekdayAvailabilities, conflictOptions, hasChildrenActive, strollerActive, bookablePeriods, maxSelectablePeople]);

  // Salle à horaires propres fermée à l'heure demandée → table non proposée
  // (le mode override manager conserve l'accès à toutes les tables)
  const zoneOpenForTable = useCallback((table: { zoneId?: string | null }) => {
    if (forceOverride || !selectedDate || !selectedTimeSlot) return true;
    return isZoneOpenForReservation(
      table.zoneId ?? null,
      selectedDate,
      selectedTimeSlot,
      customDuration ?? reservationDurationMinutes,
      zoneWindows,
      activeZoneIds
    );
  }, [forceOverride, selectedDate, selectedTimeSlot, customDuration, reservationDurationMinutes, zoneWindows, activeZoneIds]);

  // Filter tables based on selected date and number of people
  // Supports TIME mode when reservationSettings is provided
  const filteredAvailableTables = useMemo(() => {
    const blockedIds = getBlockedTableIds(allReservations, combinedTables, selectedDate, selectedTimeSlot, conflictOptions);
    
    return availableTables.filter(table => {
      if (blockedIds.has(table.id)) return false;
      if (hasChildrenActive && table.allowsChildren === false) return false;
      if (strollerActive && table.allowsStroller === false) return false;
      if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, bookablePeriods)) return false;
      if (!zoneOpenForTable(table)) return false;
      
      const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
      const isAvailable = isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null);
      return isAvailable;
    });
  }, [availableTables, selectedDate, availabilities, weekdayAvailabilities, numberOfPeople, allReservations, combinedTables, selectedTimeSlot, conflictOptions, hasChildrenActive, strollerActive, bookablePeriods, zoneOpenForTable]);

  // Filter combined tables based on selected date and number of people
  // Supports TIME mode when reservationSettings is provided
  const filteredCombinedTables = useMemo(() => {
    const blockedIds = getBlockedTableIds(allReservations, combinedTables, selectedDate, selectedTimeSlot, conflictOptions);
    
    return combinedTables.filter(ct => {
      if (blockedIds.has(ct.id)) return false;
      if (!ct.isActive) return false;
      if (hasChildrenActive && ct.allowsChildren === false) return false;
      if (strollerActive && ct.allowsStroller === false) return false;

      // Vérifier que toutes les tables de la combinaison sont disponibles à cette date
      return ct.tableIds.every(tid => {
        const table = availableTables.find(t => t.id === tid);
        if (!table) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
        if (isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods)) return false;
        if (!zoneOpenForTable(table)) return false;
        const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
        return isTableAvailableOnDate(
          table,
          selectedDate,
          availabilities,
          weekdayAvail || null
        );
      });
    });
  }, [combinedTables, selectedDate, availabilities, weekdayAvailabilities, numberOfPeople, allReservations, availableTables, selectedTimeSlot, conflictOptions, hasChildrenActive, strollerActive, bookablePeriods, zoneOpenForTable]);

  // Afficher les titres d'espace dès que les tables proposées couvrent plusieurs espaces.
  const spacesInList = useMemo(
    () => groupTablesBySpace<{ isTerrace?: boolean; zoneId?: string | null }>([...filteredAvailableTables, ...filteredCombinedTables], bookableZones).length > 1,
    [filteredAvailableTables, filteredCombinedTables, bookableZones]
  );

  // Calcul de la table optimale recommandée
  // Supports TIME mode when reservationSettings is provided
  const optimalSuggestion = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot || !numberOfPeople) return null;
    
    const adultsCount = childrenEnabled ? Math.max(1, parseInt(numberOfPeople) - numberOfChildren) : parseInt(numberOfPeople);
    return findOptimalTable(
      adultsCount,
      selectedDate,
      selectedTimeSlot,
      availableTables,
      combinedTables,
      allReservations,
      availabilities,
      weekdayAvailabilities,
      conflictOptions,
      bookablePeriods,
      childrenEnabled ? numberOfChildren : 0,
      capacityMode,
      hasStroller
    );
  }, [selectedDate, selectedTimeSlot, numberOfPeople, numberOfChildren, hasStroller, childrenEnabled, availableTables, combinedTables, allReservations, availabilities, weekdayAvailabilities, conflictOptions, bookablePeriods]);

  // Pré-sélectionner la table recommandée, ou fallback sur la première table disponible
  useEffect(() => {
    if (!open || selectedTableId) return;
    
    if (optimalSuggestion) {
      setSelectedTableId(optimalSuggestion.id);
      setSelectedTableType(optimalSuggestion.type);
    } else if (filteredAvailableTables.length > 0) {
      // Fallback: auto-select first available single table
      setSelectedTableId(filteredAvailableTables[0].id);
      setSelectedTableType('single');
    } else if (filteredCombinedTables.length > 0) {
      // Fallback: auto-select first available combined table
      setSelectedTableId(filteredCombinedTables[0].id);
      setSelectedTableType('combined');
    }
  }, [open, optimalSuggestion, selectedTableId, filteredAvailableTables, filteredCombinedTables]);

  const filteredClients = useMemo(() => {
    return filterClients(existingClients, searchTerm);
  }, [existingClients, searchTerm]);

  const handleClientSelect = (client: CustomerData) => {
    setSelectedClient(client);
    setCustomerFirstName(client.firstName);
    setCustomerLastName(client.lastName);
    setCustomerEmail(client.email);
    setCustomerPhone(client.phone || "");
  };

  const handleReset = () => {
    setClientMode("new");
    setSearchTerm("");
    setSelectedClient(null);
    setSelectedDate(initialDate);
    setSelectedTimeSlot(initialTimeSlot);
    setCustomerFirstName("");
    setCustomerLastName("");
    setCustomerEmail("");
    setCustomerPhone("");
    setNumberOfPeople("2");
    setNumberOfChildren(0);
    setHasStroller(false);
    setCustomDuration(null);
    setSource("");
    setSelectedTableId("");
    setSelectedTableType('single');
    setNotes("");
    setInternalNotes("");
    setIsRetroactive(false);
    setForceOverride(false);
    setCustomAnswers([]);
  };

  // Check if number of people exceeds public limit (for visual warning only)
  const isExceedingMaxPeople = maxPeople && parseInt(numberOfPeople || '0') > maxPeople;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Protection contre les double-clics
    if (isSubmitting) return;

    // Garde-fou UX : confirmation explicite en mode manager override
    if (forceOverride) {
      const ok = window.confirm(
        "Mode manager override : la réservation sera créée même si le créneau, le jour ou la table sont fermés / non-réservables. Aucun email ni lien client ne sera envoyé.\n\nConfirmer la création ?"
      );
      if (!ok) return;
    }


    try {
      const parsedNumberOfPeople = parseInt(numberOfPeople);
      
      // Log manager override if exceeding public limit (no blocking - server handles validation)
      if (maxPeople && parsedNumberOfPeople > maxPeople) {
        console.log(`Manager creating reservation exceeding public limit: ${parsedNumberOfPeople} > ${maxPeople}`);
      }
      
      const validatedData = reservationSchema.parse({
        customerFirstName,
        customerLastName,
        customerEmail,
        customerPhone,
        numberOfPeople: parsedNumberOfPeople,
        tableId: selectedTableType === 'single' ? selectedTableId : undefined,
        combinedTableId: selectedTableType === 'combined' ? selectedTableId : undefined,
        notes: notes || undefined,
        internalNotes: internalNotes || undefined,
      });

      setIsSubmitting(true);

      // Attendre la réponse du serveur avant de fermer le dialogue
      await onReservationCreate({
        customerFirstName: validatedData.customerFirstName,
        customerLastName: validatedData.customerLastName,
        customerEmail: validatedData.customerEmail || undefined,
        customerPhone: validatedData.customerPhone,
        numberOfPeople: childrenEnabled ? validatedData.numberOfPeople - numberOfChildren : validatedData.numberOfPeople,
        numberOfChildren,
        hasStroller,
        terracePreference: terraceEnabled ? terracePreference : undefined,
        tableId: validatedData.tableId,
        combinedTableId: validatedData.combinedTableId,
        startTime: selectedTimeSlot,
        date: selectedDate,
        notes: validatedData.notes,
        internalNotes: validatedData.internalNotes,
        durationMinutes: customDuration || undefined,
        source: source || undefined,
        ...(applicableQuestion && normalizeAnswers(applicableQuestion, customAnswers).length > 0
          ? {
              customAnswers: {
                question: applicableQuestion.question,
                answers: normalizeAnswers(applicableQuestion, customAnswers),
              },
            }
          : {}),
        ...(isRetroactive
          ? { status: 'arrivé', isRetroactive: true, skipEmails: true, skipTokens: true }
          : {}),
        ...(forceOverride
          ? { forceOverride: true, skipEmails: true, skipTokens: true }
          : {}),
      });

      // Fermer seulement après succès
      handleReset();
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast({
          title: "Erreur de validation",
          description: firstError.message,
          variant: "destructive",
        });
      }
      // Les erreurs API sont gérées par Dashboard.tsx/Availability.tsx
      // Le dialogue reste ouvert en cas d'erreur pour permettre de réessayer
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>Nouvelle réservation</DialogTitle>
            {reservationSettings && (
              <Badge variant="outline" className="text-xs font-normal">
                {isTimeMode 
                  ? `Mode horaires • ${reservationDurationMinutes} min`
                  : 'Mode créneaux'}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Date and Time Selection */}
            {readOnlyMode ? (
              // MODE SIMPLIFIÉ - Résumé en lecture seule
              <div className="space-y-4 bg-muted/50 p-4 rounded-lg border">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Résumé de la réservation
                </h3>
                
                <div className="space-y-3">
                  {/* Date */}
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="font-medium">{format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}</p>
                    </div>
                  </div>

                  {/* Heure/Créneau */}
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {isTimeMode ? 'Heure de réservation' : 'Créneau horaire'}
                      </p>
                      <p className="font-medium">
                        {isTimeMode 
                          ? `${selectedTimeSlot} → ${getReservationEndTime(selectedTimeSlot, reservationDurationMinutes)}`
                          : selectedTimeSlot}
                      </p>
                    </div>
                  </div>

                  {/* Nombre de personnes */}
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Nombre de personnes</p>
                      <p className="font-medium">{numberOfPeople} personne{parseInt(numberOfPeople) > 1 ? "s" : ""}</p>
                    </div>
                  </div>


                  {/* Enfants */}
                  {childrenEnabled && numberOfChildren > 0 && (
                    <div className="flex items-center gap-3">
                      <Baby className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Enfants</p>
                        <p className="font-medium">{numberOfChildren} enfant{numberOfChildren > 1 ? "s" : ""}</p>
                      </div>
                    </div>
                  )}

                  {/* Poussette */}
                  {strollerEnabled && hasStroller && (
                    <div className="flex items-center gap-3">
                      <StrollerIcon size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Poussette</p>
                        <p className="font-medium">Oui</p>
                      </div>
                    </div>
                  )}

                  {/* Préférence terrasse */}
                  {terraceEnabled && terracePreference !== 'no_preference' && (
                    <div className="flex items-center gap-3">
                      <span className="w-4 h-4 flex items-center justify-center text-muted-foreground">🌿</span>
                      <div>
                        <p className="text-xs text-muted-foreground">Placement</p>
                        <p className="font-medium">{terracePreference === 'terrace' ? 'En terrasse' : 'En intérieur'}</p>
                      </div>
                    </div>
                  )}

                  {/* Table sélectionnée */}
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Table</p>
                      <p className="font-medium">
                        {selectedTableType === 'single' 
                          ? (availableTables.find(t => t.id === selectedTableId)?.name || preselectedTable?.name || "Table non trouvée")
                          : (combinedTables.find(ct => ct.id === selectedTableId)?.name || preselectedCombinedTable?.name || "Table combinée non trouvée")
                        }
                        {" "}({selectedTableType === 'single'
                          ? (availableTables.find(t => t.id === selectedTableId)?.capacity || preselectedTable?.capacity || 0)
                          : (combinedTables.find(ct => ct.id === selectedTableId)?.totalCapacity || preselectedCombinedTable?.totalCapacity || 0)
                        } places)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // MODE COMPLET - Sélecteurs modifiables
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                {/* Toggle saisie rétroactive (manager) — replié par défaut */}
                <div className="flex items-start justify-between gap-4 rounded-md border border-dashed border-border/60 bg-background/60 p-3">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="retroactive-toggle" className="text-sm font-medium cursor-pointer">
                      Saisie rétroactive (réservation passée)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Enregistre une réservation dans le passé (jusqu'à 90 jours). Statut forcé à « arrivé », aucun email ni lien client envoyés.
                    </p>
                  </div>
                  <Switch
                    id="retroactive-toggle"
                    checked={isRetroactive}
                    onCheckedChange={(v) => {
                      setIsRetroactive(v);
                      // Reset la date si on désactive et qu'on est dans le passé
                      if (!v) {
                        const today = new Date(new Date().setHours(0, 0, 0, 0));
                        if (selectedDate < today) setSelectedDate(today);
                      }
                    }}
                  />
                </div>
                {isRetroactive && (
                  <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
                      Mode rétroactif actif : la réservation sera enregistrée avec le statut <strong>arrivé</strong>. Aucun email ni SMS ne sera envoyé au client.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Toggle mode manager override (créneau fermé / table non-réservable) */}
                <div className="flex items-start justify-between gap-4 rounded-md border border-dashed border-orange-300 dark:border-orange-800 bg-orange-50/40 dark:bg-orange-950/20 p-3">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="force-override-toggle" className="text-sm font-medium cursor-pointer">
                      Mode manager override (créneau / jour / table fermés)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Autorise la création dans une fermeture d'établissement, un créneau fermé, une terrasse fermée ou sur une table non-réservable. Aucun email ni lien client envoyés. Réservé aux admin / staff.
                    </p>
                  </div>
                  <Switch
                    id="force-override-toggle"
                    checked={forceOverride}
                    onCheckedChange={(v) => setForceOverride(v)}
                  />
                </div>
                {forceOverride && (
                  <Alert className="border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800">
                    <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    <AlertDescription className="text-orange-800 dark:text-orange-200 text-sm">
                      Mode manager override actif : les vérifications de fermeture (établissement, créneau, terrasse) et de table non-réservable sont désactivées. Aucun email ne sera envoyé. À utiliser uniquement en cas exceptionnel.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="date"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !selectedDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? format(selectedDate, "PPP", { locale: fr }) : "Sélectionner une date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            if (date) {
                              setSelectedDate(date);
                              setSelectedTableId(""); // Reset table selection when date changes
                            }
                          }}
                          initialFocus
                          locale={fr}
                          disabled={(date) => {
                            const today = new Date(new Date().setHours(0, 0, 0, 0));
                            if (isRetroactive) {
                              const minDate = new Date(today);
                              minDate.setDate(minDate.getDate() - 90);
                              return date < minDate || date > today;
                            }
                            // Mode manager override et mode standard : autorise toute date future
                            // (les fermetures futures restent bypassées côté serveur si forceOverride).
                            return date < today;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeSlot">
                      {isTimeMode ? 'Heure de réservation *' : 'Créneau horaire *'}
                    </Label>
                    {isTimeMode ? (
                      <div className="pt-1">
                        <TimeBookingGrid
                          availableTimes={timeAvailabilityForDialog}
                          selectedTime={selectedTimeSlot}
                          onTimeSelect={(time) => {
                            setSelectedTimeSlot(time);
                            setSelectedTableId(""); // Reset table selection when time changes
                          }}
                        />
                      </div>
                    ) : (
                      <Select value={selectedTimeSlot} onValueChange={(value) => {
                        setSelectedTimeSlot(value);
                        setSelectedTableId(""); // Reset table selection when time slot changes
                      }}>
                        <SelectTrigger id="timeSlot">
                          <SelectValue placeholder="Sélectionner un créneau" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => {
                            // Trouver les infos de disponibilité pour ce créneau
                            const slotInfo = availableTimeSlots.find(s => s.start === slot.start);
                            const isAvailable = slotInfo?.isAvailable || false;
                            
                            return (
                              <SelectItem 
                                key={`${slot.start}-${slot.end}`} 
                                value={slot.start}
                                disabled={!isAvailable}
                              >
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  {slot.start} - {slot.end}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="persons">Nombre de personnes {childrenEnabled && "(enfants inclus)"} *</Label>
                    {isExceedingMaxPeople && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Dépasse la limite publique ({maxPeople})
                      </Badge>
                    )}
                  </div>
                  <Select value={numberOfPeople} onValueChange={(value) => {
                    setNumberOfPeople(value);
                    setSelectedTableId(""); // Reset table selection when persons change
                  }}>
                    <SelectTrigger id="persons">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePersonCounts.map(({ count, isAvailable, hasMatchingTable }) => (
                        <SelectItem 
                          key={count} 
                          value={count.toString()}
                        >
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {count} personne{count > 1 ? "s" : ""}
                            {!hasMatchingTable && (
                              <span className="text-xs text-amber-600 dark:text-amber-400 ml-2">(aucune table adaptée)</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Children selector - only in non-readOnly mode */}
{!readOnlyMode && childrenEnabled && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Baby className="w-4 h-4" />
                      Dont enfants
                    </Label>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={numberOfChildren <= 0} onClick={() => setNumberOfChildren(Math.max(0, numberOfChildren - 1))}>
                        <span className="text-lg">−</span>
                      </Button>
                      <span className="w-8 text-center font-medium text-sm">{numberOfChildren}</span>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={numberOfChildren >= parseInt(numberOfPeople) - 1} onClick={() => setNumberOfChildren(Math.min(parseInt(numberOfPeople) - 1, numberOfChildren + 1))}>
                        <span className="text-lg">+</span>
                      </Button>
                    </div>
                    {numberOfChildren > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Sur {numberOfPeople} personne{parseInt(numberOfPeople) > 1 ? "s" : ""}, {parseInt(numberOfPeople) - numberOfChildren} adulte{parseInt(numberOfPeople) - numberOfChildren > 1 ? "s" : ""} et {numberOfChildren} enfant{numberOfChildren > 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                )}

                {/* Stroller checkbox */}
                {!readOnlyMode && strollerEnabled && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="hasStroller"
                      checked={hasStroller}
                      onCheckedChange={(checked) => setHasStroller(checked === true)}
                    />
                    <Label htmlFor="hasStroller" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                      <StrollerIcon size={14} /> Poussette
                    </Label>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Tables disponibles *</Label>
                  {filteredAvailableTables.length === 0 && filteredCombinedTables.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground bg-muted/50 rounded-lg border-2 border-dashed">
                      Aucune table disponible pour ce créneau.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Table recommandée */}
                      {optimalSuggestion ? (
                        <div className="p-4 bg-green-50 dark:bg-green-950/20 border-2 border-green-500 rounded-lg">
                          <div className="flex items-start gap-3">
                            <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 space-y-2">
                              <span className="font-semibold text-green-900 dark:text-green-100">
                                Table recommandée
                              </span>
                              <p className="text-sm text-green-800 dark:text-green-200">
                                <span className="font-medium">{optimalSuggestion.name}</span> • {optimalSuggestion.capacity} personnes
                              </p>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                  setSelectedTableId(optimalSuggestion.id);
                                  setSelectedTableType(optimalSuggestion.type);
                                }}
                                className="w-full bg-green-600 hover:bg-green-700 text-white"
                              >
                                Utiliser cette table
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (filteredAvailableTables.length > 0 || filteredCombinedTables.length > 0) ? (
                        <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700">
                          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                          <AlertDescription className="text-amber-800 dark:text-amber-200">
                            Aucune table ne correspond exactement. Une table a été pré-sélectionnée, mais vous pouvez en choisir une autre ci-dessous.
                          </AlertDescription>
                        </Alert>
                      ) : null}

                      {/* Tables groupées par espace (présentation uniquement) */}
                      {/* Tables simples */}
                      {filteredAvailableTables.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Toutes les tables disponibles</p>
                          {groupTablesBySpace(filteredAvailableTables, bookableZones).map((group, _gi, all) => (
                            <div key={group.key} className="space-y-1">
                              {(all.length > 1 || spacesInList) && <p className="text-xs font-bold uppercase tracking-wide text-foreground">{group.label}</p>}
                              <div className="flex flex-wrap gap-2">
                              {group.items.map((table) => (
                              <button
                                key={table.id}
                                type="button"
                                onClick={() => {
                                  setSelectedTableId(table.id);
                                  setSelectedTableType('single');
                                }}
                                className={`px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
                                  selectedTableId === table.id && selectedTableType === 'single'
                                    ? "border-primary bg-primary text-primary-foreground shadow-md"
                                    : "border-border hover:border-primary/50 hover:bg-accent"
                                }`}
                              >
                              <div className="flex flex-col items-start gap-1">
                                  <span className="font-semibold">{table.name}</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs opacity-80">
                                      {table.capacity} pers.
                                    </span>
                                    {table.capacity < parseInt(numberOfPeople) && (
                                      <Badge variant="warning" className="text-[10px] px-1.5 py-0">
                                        Sous-capacité
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tables combinées */}
                      {filteredCombinedTables.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Tables combinées</p>
                          {groupTablesBySpace(filteredCombinedTables, bookableZones).map((group, _gi, all) => (
                            <div key={group.key} className="space-y-1">
                              {(all.length > 1 || spacesInList) && <p className="text-xs font-bold uppercase tracking-wide text-foreground">{group.label}</p>}
                              <div className="flex flex-wrap gap-2">
                              {group.items.map((ct) => (
                              <button
                                key={ct.id}
                                type="button"
                                onClick={() => {
                                  setSelectedTableId(ct.id);
                                  setSelectedTableType('combined');
                                }}
                                className={`px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
                                  selectedTableId === ct.id && selectedTableType === 'combined'
                                    ? "border-primary bg-primary text-primary-foreground shadow-md"
                                    : "border-border hover:border-primary/50 hover:bg-accent"
                                }`}
                              >
                                <div className="flex flex-col items-start gap-0.5">
                                  <span className="font-semibold">{ct.name}</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs opacity-80">
                                      {ct.totalCapacity} pers.
                                    </span>
                                    {ct.totalCapacity < parseInt(numberOfPeople) && (
                                      <Badge variant="warning" className="text-[10px] px-1.5 py-0">
                                        Sous-capacité
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Client Selection Tabs - Only show if a table is selected */}
            {selectedTableId && (
              <>
                <Tabs value={clientMode} onValueChange={(value: any) => setClientMode(value)}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="new">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau client
                    </TabsTrigger>
                    <TabsTrigger value="existing">
                      <User className="w-4 h-4 mr-2" />
                      Client existant
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="existing" className="space-y-4">
                    {/* Search existing clients */}
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher un client par nom, email ou téléphone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {/* Client list */}
                    <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                      {filteredClients.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                          <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>Aucun client trouvé</p>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {filteredClients.map((client) => (
                            <div
                              key={client.id || client.email}
                              className={`p-3 cursor-pointer hover:bg-muted transition-colors ${
                                selectedClient?.email === client.email ? "bg-muted" : ""
                              }`}
                              onClick={() => handleClientSelect(client)}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{client.firstName} {client.lastName}</p>
                                  <p className="text-sm text-muted-foreground">{client.email}</p>
                                  {client.phone && <p className="text-sm text-muted-foreground">{client.phone}</p>}
                                </div>
                                <div className="text-right">
                                  <Badge variant="secondary">
                                    {client.totalReservations} réservation{client.totalReservations > 1 ? "s" : ""}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {selectedClient && (
                      <div className="space-y-3">
                        <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                            <User className="w-4 h-4" />
                            <span className="font-medium">Client sélectionné : {selectedClient.firstName} {selectedClient.lastName}</span>
                          </div>
                        </div>
                        
                        {/* Client notes banner */}
                        {selectedClient.notes && (
                          <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-800 dark:text-amber-200">
                              <p className="font-medium mb-1">Note client :</p>
                              <p className="whitespace-pre-wrap text-sm">{selectedClient.notes}</p>
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="new" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">Prénom</Label>
                        <Input
                          id="firstName"
                          value={customerFirstName}
                          onChange={(e) => setCustomerFirstName(e.target.value)}
                          placeholder="Jean"
                          maxLength={50}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Nom</Label>
                        <Input
                          id="lastName"
                          value={customerLastName}
                          onChange={(e) => setCustomerLastName(e.target.value)}
                          placeholder="Dupont"
                          maxLength={50}
                        />
                        <p className="text-xs text-muted-foreground">Au moins un prénom ou nom requis</p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 mb-2">
                          <Checkbox 
                            id="noEmail" 
                            checked={noEmail} 
                            onCheckedChange={(checked) => {
                              setNoEmail(checked === true);
                              if (checked) setCustomerEmail("");
                            }}
                          />
                          <Label htmlFor="noEmail" className="text-sm cursor-pointer flex items-center gap-1">
                            <PhoneOff className="w-3 h-3" />
                            Client sans email
                          </Label>
                        </div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={customerEmail}
                          onChange={(e) => setCustomerEmail(e.target.value)}
                          placeholder="jean.dupont@email.com"
                          maxLength={255}
                          disabled={noEmail}
                          className={noEmail ? "bg-muted" : ""}
                        />
                        {noEmail && (
                          <Alert variant="default" className="mt-2 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
                            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
                              Sans email, le client ne recevra aucune confirmation, rappel ou suivi automatique.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="+33612345678"
                        maxLength={20}
                      />
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Reservation Details - Always visible when table selected */}
                {(clientMode === "new" || selectedClient) && (
                  <>
                    {/* Terrace Preference */}
                    {!readOnlyMode && terraceEnabled && (
                      <TerracePreferenceSelector
                        value={terracePreference}
                        onChange={setTerracePreference}
                        compact
                      />
                    )}
                    {/* Blacklist / Allergy alerts */}
                    {(() => {
                      const emailToCheck = clientMode === "existing" ? selectedClient?.email : customerEmail;
                      if (!emailToCheck) return null;
                      const record = customerNotes.get(emailToCheck.toLowerCase());
                      if (!record) return null;
                      return (
                        <>
                          {record.isBlacklisted && (
                            <Alert className="border-destructive bg-destructive/10">
                              <AlertTriangle className="h-4 w-4 text-destructive" />
                              <AlertDescription className="text-destructive font-medium">
                                ⚠️ Ce client est blacklisté
                              </AlertDescription>
                            </Alert>
                          )}
                          {record.hasNoShow && (
                            <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                              <AlertDescription className="text-amber-700 dark:text-amber-400 font-medium">
                                Ce client a un historique de no-show
                              </AlertDescription>
                            </Alert>
                          )}
                          {record.allergiesTags && record.allergiesTags.length > 0 && (
                            <Alert className="border-orange-400 bg-orange-50 dark:bg-orange-950/20">
                              <AlertTriangle className="h-4 w-4 text-orange-600" />
                              <AlertDescription className="text-orange-700 dark:text-orange-400">
                                <span className="font-medium">Allergies :</span> {record.allergiesTags.join(', ')}
                                {record.allergiesNotes && <p className="text-xs mt-1">{record.allergiesNotes}</p>}
                              </AlertDescription>
                            </Alert>
                          )}
                        </>
                      );
                    })()}
                    {/* Customer persistent notes */}
                    {(() => {
                      const emailToCheck = clientMode === "existing" ? selectedClient?.email : customerEmail;
                      const existingNotes = getNotesForEmail(emailToCheck);
                      const customerId = getCustomerIdForEmail(emailToCheck);
                      if (!emailToCheck || (!existingNotes && !editingCustomerNotes)) return null;
                      return (
                        <div className="space-y-2 p-3 rounded-lg border-2 border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <StickyNote className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                              <Label className="text-violet-700 dark:text-violet-300 font-medium">Notes client (persistantes)</Label>
                            </div>
                            {!editingCustomerNotes && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-violet-600 dark:text-violet-400 hover:text-violet-800"
                                onClick={() => {
                                  setEditingCustomerNotes(true);
                                  setCustomerNotesValue(existingNotes || "");
                                }}
                              >
                                <Pencil className="w-3 h-3 mr-1" />
                                Modifier
                              </Button>
                            )}
                          </div>
                          {editingCustomerNotes ? (
                            <div className="space-y-2">
                              <Textarea
                                value={customerNotesValue}
                                onChange={(e) => setCustomerNotesValue(e.target.value)}
                                placeholder="Préférences, allergies, notes importantes..."
                                className="min-h-[60px] border-violet-300 dark:border-violet-700"
                                maxLength={1000}
                              />
                              <div className="flex gap-2 justify-end">
                                <Button type="button" variant="ghost" size="sm" onClick={() => setEditingCustomerNotes(false)}>
                                  Annuler
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={savingCustomerNotes}
                                  className="bg-violet-600 hover:bg-violet-700 text-white"
                                  onClick={async () => {
                                    if (!customerId || !resolvedEstablishmentId) return;
                                    setSavingCustomerNotes(true);
                                    try {
                                      const { error } = await supabase
                                        .from('customers')
                                        .update({ notes: customerNotesValue || null })
                                        .eq('id', customerId)
                                        .eq('establishment_id', resolvedEstablishmentId);
                                      if (error) throw error;
                                      await refetchCustomerNotes();
                                      setEditingCustomerNotes(false);
                                      toast({ title: "Notes client mises à jour" });
                                    } catch {
                                      toast({ title: "Erreur", description: "Impossible de sauvegarder les notes", variant: "destructive" });
                                    } finally {
                                      setSavingCustomerNotes(false);
                                    }
                                  }}
                                >
                                  {savingCustomerNotes ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                  Enregistrer
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-violet-800 dark:text-violet-200 whitespace-pre-wrap">{existingNotes}</p>
                          )}
                          <p className="text-xs text-violet-600/70 dark:text-violet-400/70">
                            Ces notes sont liées au client et s'afficheront pour toutes ses réservations.
                          </p>
                        </div>
                      );
                    })()}

                    <div className="space-y-2">
                      <Label htmlFor="notes">Note de réservation</Label>
                      <Input
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Demande spéciale pour cette réservation..."
                        maxLength={500}
                      />
                      <p className="text-xs text-muted-foreground">
                        Note ajoutée à la réservation.
                      </p>
                    </div>

                    {applicableQuestion && (
                      <EstablishmentQuestionField
                        question={applicableQuestion}
                        answers={customAnswers}
                        onAnswersChange={setCustomAnswers}
                        staffMode
                        idPrefix="new-reservation-question"
                      />
                    )}


                    <div className="space-y-2">
                      <Label htmlFor="internalNotes">Note interne</Label>
                      <Textarea
                        id="internalNotes"
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        placeholder="Notes internes réservées à l'équipe..."
                        maxLength={1000}
                        className="min-h-[80px]"
                      />
                      <p className="text-xs text-muted-foreground">
                        Visible uniquement par l'équipe. Liée à cette réservation uniquement.
                      </p>
                    </div>

                    {/* Source de la réservation */}
                    <div className="space-y-2">
                      <Label htmlFor="source">Source de la réservation</Label>
                      <Select value={source || "none"} onValueChange={(val) => setSource(val === "none" ? "" : val)}>
                        <SelectTrigger id="source">
                          <SelectValue placeholder="Sélectionner une source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non renseignée</SelectItem>
                          <SelectItem value="Google">Google</SelectItem>
                          <SelectItem value="Site web">Site web</SelectItem>
                          <SelectItem value="Instagram">Instagram</SelectItem>
                          <SelectItem value="Facebook">Facebook</SelectItem>
                          <SelectItem value="TheFork">TheFork</SelectItem>
                          <SelectItem value="Téléphone">Téléphone</SelectItem>
                          <SelectItem value="Passage">Passage</SelectItem>
                          <SelectItem value="Bouche à oreille">Bouche à oreille</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        D'où vient ce client ? (Google, réseaux sociaux, téléphone, etc.)
                      </p>
                    </div>
                    {/* Durée personnalisée (mode TIME uniquement) */}
                    {isTimeMode && (
                      <div className="space-y-2">
                        <Label htmlFor="customDuration">Durée de la réservation</Label>
                        <Select
                          value={customDuration ? customDuration.toString() : "default"}
                          onValueChange={(val) => setCustomDuration(val === "default" ? null : parseInt(val))}
                        >
                          <SelectTrigger id="customDuration">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Par défaut ({reservationDurationMinutes} min)</SelectItem>
                            <SelectItem value="30">30 min</SelectItem>
                            <SelectItem value="45">45 min</SelectItem>
                            <SelectItem value="60">1h</SelectItem>
                            <SelectItem value="75">1h15</SelectItem>
                            <SelectItem value="90">1h30</SelectItem>
                            <SelectItem value="105">1h45</SelectItem>
                            <SelectItem value="120">2h</SelectItem>
                            <SelectItem value="150">2h30</SelectItem>
                            <SelectItem value="180">3h</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Modifier la durée permet d'optimiser l'utilisation des tables.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                handleReset();
                onOpenChange(false);
              }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                (!customerFirstName && !customerLastName) ||
                !selectedTableId
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création en cours...
                </>
              ) : (
                'Créer la réservation'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
